'use client';

import type { IEnglishIqItemV2 } from '@repo/sdk';
import { useMemo, useState } from 'react';

type ReorderTokenType = Extract<IEnglishIqItemV2, { type: 'reorder' }>['tokens'][number];

interface IEnglishIqV2ReorderAnswerProps {
  availableLabel: string;
  clearLabel: string;
  disabled: boolean;
  onSubmit: (tokenIds: string[]) => void;
  selectedLabel: string;
  submitLabel: string;
  tokens: ReorderTokenType[];
}

export function EnglishIqV2ReorderAnswer({
  availableLabel,
  clearLabel,
  disabled,
  onSubmit,
  selectedLabel,
  submitLabel,
  tokens,
}: IEnglishIqV2ReorderAnswerProps): React.JSX.Element {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const available = useMemo(
    () => tokens.filter((token) => !selectedIds.includes(token.id)),
    [selectedIds, tokens],
  );
  const selected = selectedIds.flatMap((id) => tokens.filter((token) => token.id === id));
  return (
    <div className="space-y-5" dir="ltr">
      <TokenArea label={selectedLabel} tokens={selected} onSelect={removeToken} />
      <TokenArea label={availableLabel} tokens={available} onSelect={addToken} />
      <div className="flex flex-wrap gap-3">
        <button
          className="min-h-12 rounded-full bg-[#ff7a1a] px-6 font-bold text-[#160c06] disabled:opacity-40"
          disabled={disabled || selectedIds.length !== tokens.length}
          onClick={() => onSubmit(selectedIds)}
          type="button"
        >
          {submitLabel}
        </button>
        <button
          className="min-h-12 rounded-full border border-[#f7edda]/20 px-6 font-semibold text-[#fff8e9] disabled:opacity-40"
          disabled={disabled || selectedIds.length === 0}
          onClick={() => setSelectedIds([])}
          type="button"
        >
          {clearLabel}
        </button>
      </div>
    </div>
  );

  function addToken(id: string): void {
    if (!disabled) {
      setSelectedIds((current) => [...current, id]);
    }
  }

  function removeToken(id: string): void {
    if (!disabled) {
      setSelectedIds((current) => current.filter((candidate) => candidate !== id));
    }
  }
}

interface ITokenAreaProps {
  label: string;
  onSelect: (id: string) => void;
  tokens: ReorderTokenType[];
}

function TokenArea({ label, onSelect, tokens }: ITokenAreaProps): React.JSX.Element {
  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-[#ffb36b]">{label}</p>
      <div className="flex min-h-16 flex-wrap content-start gap-2 rounded-2xl border border-[#f7edda]/15 bg-black/20 p-3">
        {tokens.map((token) => (
          <button
            className="min-h-10 rounded-xl border border-[#f7edda]/20 bg-[#f7edda]/10 px-4 font-medium text-[#fff8e9] hover:border-[#ff7a1a] focus-visible:outline-2 focus-visible:outline-[#ff7a1a]"
            key={token.id}
            onClick={() => onSelect(token.id)}
            type="button"
          >
            {token.text}
          </button>
        ))}
      </div>
    </div>
  );
}
